import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AssignButton } from '../components/AssignTask';
import { useQueryClient } from '@tanstack/react-query';
import { Clapperboard, FileText, Folder, FolderHeart, FolderOpen, FolderPlus, Globe, Pencil, Trash2, Users, X } from 'lucide-react';
import { formatDuration, frameRateLabel } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useCollectionItems, useCollections, useMe, usePeopleSearch, type CollectionDTO } from '../api/hooks';
import { SCOPE_LABEL, flattenTree } from '../components/AddToCollection';
import { Modal } from '../components/Modal';
import { RunWorkflowButton } from '../components/RunWorkflow';
import { AssetThumb } from '../components/SpriteThumb';
import { AssetStatus } from '../components/Status';
import { toast } from '../components/Toasts';
import { relative } from '../lib/format';
import { useDebounced } from '../lib/useDebounced';
import { EmptyState } from '../components/EmptyState';
import { queryKeys } from '../api/queryKeys';
import { LoadMore } from '../components/LoadMore';

/** Your own folders of assets and sequences (edits); nobody else sees them. */
export function Collections() {
  const q = useCollections();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const items = q.data?.items ?? [];
  const sections = (['personal', 'shared', 'public'] as const).map((scope) => ({ scope, tree: flattenTree(items.filter((c) => c.scope === scope)) }));
  const tree = sections.flatMap((s) => s.tree);
  const selectedId = params.get('c') ?? tree[0]?.id ?? null;
  const selected = items.find((c) => c.id === selectedId) ?? null;
  const [editing, setEditing] = useState<{ mode: 'new' | 'edit'; parentId: string | null; visibility?: 'personal' | 'public' } | null>(null);
  const [sharing, setSharing] = useState<CollectionDTO | null>(null);
  const select = (id: string | null) => setParams(id ? { c: id } : {}, { replace: true });

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: queryKeys.collections() }), qc.invalidateQueries({ queryKey: ['collection-items'] }), qc.invalidateQueries({ queryKey: ['collections-containing'] })]);

  async function remove(c: CollectionDTO) {
    const subs = items.filter((x) => x.parentId === c.id).length;
    if (!window.confirm(`Delete the collection “${c.name}”${subs ? ` and its subfolders` : ''}? The assets and sequences in it are not deleted.`)) return;
    try {
      await api(`/api/v1/collections/${c.id}`, { method: 'DELETE' });
      select(c.parentId);
      await refresh();
      toast(`Deleted ${c.name}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <>
      <header className="topbar">
        <h1><FolderHeart size={16} color="var(--accent-strong)" /> Collections</h1>
        <div className="topbar-spacer" />
        {q.data?.canCreatePublic && <button className="btn" onClick={() => setEditing({ mode: 'new', parentId: null, visibility: 'public' })} data-testid="new-public-collection"><Globe size={15} /> New public collection</button>}
        <button className="btn primary" onClick={() => setEditing({ mode: 'new', parentId: null })} data-testid="new-collection"><FolderPlus size={15} /> New collection</button>
      </header>
      {q.isLoading ? <div className="page"><div className="skeleton" style={{ height: 160 }} /></div> : tree.length === 0 ? (
        <EmptyState icon={<FolderHeart size={24} />} title="Collections">
          <p>Gather assets and edits for a project, a pitch or a shortlist. Personal collections are yours, and you can share one with chosen people; public collections are seen by everyone. Add items with “Collect” on an asset, in the library, or in the editor.</p>
          <button className="btn primary" onClick={() => setEditing({ mode: 'new', parentId: null })}><FolderPlus size={15} /> New collection</button>
        </EmptyState>
      ) : (
        <div className="collections-layout">
          <nav className="collection-tree" aria-label="Collections" data-testid="collection-tree">
            {sections.filter((s) => s.tree.length).map((s) => (
              <div key={s.scope} data-testid={`collections-${s.scope}`}>
                <p className="section-label" style={{ margin: '10px 10px 4px' }}>{SCOPE_LABEL[s.scope]}</p>
                {s.tree.map((c) => (
                  <button key={c.id} className={`collection-node ${c.id === selectedId ? 'active' : ''}`} style={{ paddingLeft: 10 + c.depth * 16 }} onClick={() => select(c.id)} data-testid="collection-node">
                    {c.scope === 'public' ? <Globe size={14} /> : c.id === selectedId ? <FolderOpen size={14} /> : <Folder size={14} />}
                    <span className="name">{c.name}</span>
                    {c.scope === 'personal' && c.shares.length > 0 && <Users size={12} className="faint" aria-label="Shared" />}
                    <span className="count">{c.assets + c.sequences || ''}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>
          {selected && <CollectionView c={selected} all={items} onEdit={() => setEditing({ mode: 'edit', parentId: selected.parentId })}
            onSubfolder={() => setEditing({ mode: 'new', parentId: selected.id })} onDelete={() => void remove(selected)} onChanged={refresh} onShare={() => setSharing(selected)} />}
        </div>
      )}
      {editing && (
        <FolderDialog mode={editing.mode} folder={editing.mode === 'edit' ? selected : null} parentId={editing.parentId} all={items}
          visibility={editing.visibility ?? (editing.parentId ? items.find((x) => x.id === editing.parentId)?.scope === 'public' ? 'public' : 'personal' : 'personal')}
          onClose={() => setEditing(null)} onSaved={async (id) => { setEditing(null); await refresh(); select(id); }} />
      )}
      {sharing && <ShareDialog c={sharing} onClose={() => setSharing(null)} onSaved={async () => { setSharing(null); await refresh(); }} />}
    </>
  );
}

function CollectionView({ c, all, onEdit, onSubfolder, onDelete, onChanged, onShare }: {
  c: CollectionDTO; all: CollectionDTO[]; onEdit: () => void; onSubfolder: () => void; onDelete: () => void; onChanged: () => Promise<unknown>; onShare: () => void;
}) {
  const q = useCollectionItems(c.id);
  const navigate = useNavigate();
  const path: CollectionDTO[] = [];
  for (let p: CollectionDTO | undefined = c; p; p = all.find((x) => x.id === p!.parentId)) path.unshift(p);

  async function removeItem(body: { assetIds?: string[]; sequenceIds?: string[] }, label: string) {
    try {
      await api(`/api/v1/collections/${c.id}/items/remove`, { method: 'POST', json: body });
      await onChanged();
      toast(`Removed ${label} from ${c.name}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  const assets = q.data?.assets ?? [];
  const sequences = q.data?.sequences ?? [];
  return (
    <div className="page" data-testid="collection-view">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>
          {path.slice(0, -1).map((p) => <span key={p.id} className="faint" style={{ fontWeight: 500 }}>{p.name} / </span>)}{c.name}
        </h2>
        <span style={{ flex: 1 }} />
        {c.scope !== 'personal' && <span className="pill">{c.scope === 'public' ? 'Public' : `Shared by ${c.owner}${c.canEdit ? '' : ' · view only'}`}</span>}
        <AssignButton subject={{ type: 'collection', id: c.id, name: c.name }} />
        {c.canEdit && <button className="btn ghost sm" onClick={onSubfolder} data-testid="new-subfolder"><FolderPlus size={13} /> Subfolder</button>}
        {c.scope === 'personal' && <button className="btn ghost sm" onClick={onShare} data-testid="share-collection"><Users size={13} /> Share{c.shares.length ? ` (${c.shares.length})` : ''}</button>}
        {c.canManage && <button className="btn ghost sm" onClick={onEdit} data-testid="edit-collection"><Pencil size={13} /> Rename or move</button>}
        {c.canManage && <button className="btn ghost sm danger" onClick={onDelete} data-testid="delete-collection"><Trash2 size={13} /> Delete</button>}
      </div>
      {c.description && <p className="lede" style={{ marginTop: 8 }}>{c.description}</p>}
      {q.isLoading ? <div className="skeleton" style={{ height: 160, marginTop: 16 }} /> : assets.length + sequences.length === 0 ? (
        <p className="muted" style={{ marginTop: 24 }}>This collection is empty. Use “Collect” on an asset, in the library inspector, or in the sequence editor to add items.</p>
      ) : (
        <>
          {sequences.length > 0 && (
            <>
              <p className="section-label" style={{ marginTop: 24 }}>Sequences and edits</p>
              <table className="table" data-testid="collection-sequences">
                <thead><tr><th>Name</th><th>Duration</th><th>Format</th><th>Updated</th><th aria-label="Actions" /></tr></thead>
                <tbody>
                  {sequences.map((s) => (
                    <tr key={s.id} onClick={() => navigate(`/create/${s.id}`)}>
                      <td className="wrap" style={{ fontWeight: 560 }}><Clapperboard size={13} style={{ verticalAlign: -2, marginRight: 6 }} />{s.name}</td>
                      <td className="mono">{formatDuration(s.durationTicks)}</td>
                      <td className="muted">{s.width}×{s.height} · {frameRateLabel(s.frameRate)} fps</td>
                      <td className="muted">{relative(s.updatedAt)}{s.updatedBy ? ` · ${s.updatedBy}` : ''}</td>
                      <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <a className="btn ghost sm" href={`/api/v1/sequences/${s.id}/edl`} download title="Download a CMX 3600 EDL"><FileText size={13} /> EDL</a>
                          <RunWorkflowButton subject="sequence" id={s.id} name={s.name} />
                          {c.canEdit && <button className="btn ghost sm icon" aria-label={`Remove ${s.name} from this collection`} title="Remove from this collection" onClick={() => void removeItem({ sequenceIds: [s.id] }, s.name)}><X size={13} /></button>}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {assets.length > 0 && (
            <>
              <p className="section-label" style={{ marginTop: 24 }}>Assets</p>
              <div className="grid" style={{ padding: 0 }} data-testid="collection-assets">
                {assets.map((a) => (
                  <div key={a.id} className="card" data-testid="collection-asset" onClick={() => navigate(`/assets/${a.id}`)}>
                    <AssetThumb asset={a} />
                    {c.canEdit && <button className="btn sm icon card-remove" aria-label={`Remove ${a.title} from this collection`} title="Remove from this collection"
                      onClick={(e) => { e.stopPropagation(); void removeItem({ assetIds: [a.id] }, a.title); }}><X size={13} /></button>}
                    <div className="card-body">
                      <div className="card-title" title={a.title}>{a.title}</div>
                      <div className="card-meta"><AssetStatus status={a.status} availability={a.availability} /><span style={{ flex: 1 }} /><span>{relative(a.createdAt)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          <LoadMore list={q} shown={assets.length + sequences.length} noun="items" />
        </>
      )}
      <p className="faint" style={{ fontSize: 12, marginTop: 24 }}>
        {c.scope === 'public' ? 'Everyone in the workspace sees this collection.' : c.scope === 'shared' ? `${c.owner} shared this collection with you.` : c.shares.length ? `Shared with ${c.shares.map((s) => s.name).join(', ')}.` : 'Only you see this collection.'}
        {' '}Assets someone cannot access, or that are deleted, are not shown to them; restored assets come back.
      </p>
      <p style={{ marginTop: 4 }}><Link to="/library" className="faint" style={{ fontSize: 12 }}>Find more in the library</Link></p>
    </div>
  );
}

function FolderDialog({ mode, folder, parentId, all, visibility, onClose, onSaved }: {
  mode: 'new' | 'edit'; folder: CollectionDTO | null; parentId: string | null; all: CollectionDTO[]; visibility: 'personal' | 'public'; onClose: () => void; onSaved: (id: string) => Promise<void>;
}) {
  const [name, setName] = useState(folder?.name ?? '');
  const [description, setDescription] = useState(folder?.description ?? '');
  const [parent, setParent] = useState<string>(parentId ?? '');
  useEffect(() => setParent(parentId ?? ''), [parentId]);
  // A folder cannot move under itself or its own subfolders.
  const below = new Set<string>();
  if (folder) {
    const walk = (id: string) => { below.add(id); all.filter((x) => x.parentId === id).forEach((x) => walk(x.id)); };
    walk(folder.id);
  }
  // Folders move within their own tree: your personal ones, or the public ones.
  const scope = folder?.scope ?? (visibility === 'public' ? 'public' : 'personal');
  const options = flattenTree(all.filter((c) => c.scope === scope && c.canEdit)).filter((c) => !below.has(c.id));

  async function save() {
    try {
      const body = { name: name.trim(), description, parentId: parent || null, ...(mode === 'new' ? { visibility } : {}) };
      if (mode === 'new') {
        const r = await api<{ id: string }>('/api/v1/collections', { method: 'POST', json: body });
        await onSaved(r.id);
      } else {
        await api(`/api/v1/collections/${folder!.id}`, { method: 'PATCH', json: body });
        await onSaved(folder!.id);
      }
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <Modal title={mode === 'new' ? (visibility === 'public' ? 'New public collection' : 'New collection') : `Edit ${folder?.name}`} onClose={onClose}
      actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={!name.trim()} onClick={() => void save()} data-testid="save-collection">{mode === 'new' ? 'Create' : 'Save'}</button></>}>
      <div className="field"><label htmlFor="col-name">Name</label>
        <input id="col-name" className="input" autoFocus value={name} placeholder="Harbour story selects" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim() && void save()} /></div>
      <div className="field"><label htmlFor="col-desc">Description</label>
        <textarea id="col-desc" className="textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" /></div>
      <div className="field"><label htmlFor="col-parent">Inside</label>
        <select id="col-parent" className="select" value={parent} onChange={(e) => setParent(e.target.value)}>
          <option value="">Top level</option>
          {options.map((c) => <option key={c.id} value={c.id}>{' '.repeat(c.depth)}{c.name}</option>)}
        </select>
      </div>
    </Modal>
  );
}

/**
 * Share a personal folder (and its subfolders) with chosen people, to view or to edit. Type a name or
 * email, pick one or more people as chips, choose View or Edit and Add; the current shares are listed
 * below with a role selector and Remove. Saving replaces the folder's shares.
 */
function ShareDialog({ c, onClose, onSaved }: { c: CollectionDTO; onClose: () => void; onSaved: () => Promise<void> }) {
  const me = useMe().data;
  const [shares, setShares] = useState(c.shares.map((s) => ({ userId: s.userId, name: s.name, canEdit: s.canEdit })));
  const [picked, setPicked] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const [text, setText] = useState('');
  const [role, setRole] = useState<'view' | 'edit'>('view');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const typed = useDebounced(text.trim(), 150);
  const found = usePeopleSearch(typed).data?.items ?? [];
  // People who already have the folder, and yourself, are not offered again.
  const taken = new Set([...shares.map((s) => s.userId), ...picked.map((p) => p.id), ...(me ? [me.user.id] : [])]);
  const options = found.filter((p) => !taken.has(p.id));

  const pick = (p: { id: string; name: string; email?: string }) => { setPicked((cur) => [...cur, p]); setText(''); setOpen(false); };
  const add = () => {
    if (!picked.length) return;
    setShares((cur) => [...cur, ...picked.map((p) => ({ userId: p.id, name: p.name, canEdit: role === 'edit' }))]);
    setPicked([]);
  };
  async function save() {
    setBusy(true);
    try {
      await api(`/api/v1/collections/${c.id}/shares`, { method: 'PUT', json: { shares: shares.map(({ userId, canEdit }) => ({ userId, canEdit })) } });
      toast(shares.length ? `Shared ${c.name} with ${shares.length} ${shares.length === 1 ? 'person' : 'people'}` : `${c.name} is private again`);
      await onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title={`Share ${c.name}`} onClose={onClose} actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={busy} onClick={() => void save()} data-testid="save-shares">Save</button></>}>
      <p className="muted" style={{ marginTop: 0 }}>People you share with see this folder and its subfolders under “Shared with me”. With “Can edit” they can add and remove items; only you rename, move or delete it.</p>
      <div className="share-add" data-testid="share-add">
        <div className="share-chips input" onClick={() => document.getElementById('share-people')?.focus()}>
          {picked.map((p) => (
            <span className="chip" key={p.id} data-testid="share-chip">{p.name}<button aria-label={`Remove ${p.name}`} onClick={() => setPicked((cur) => cur.filter((x) => x.id !== p.id))}><X size={12} /></button></span>
          ))}
          <input id="share-people" role="combobox" aria-expanded={open} aria-controls="share-options" aria-label="Add people" autoComplete="off" placeholder={picked.length ? '' : 'Type a name or email'} value={text}
            onChange={(e) => { setText(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => window.setTimeout(() => setOpen(false), 120)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && options[0] && open) { e.preventDefault(); pick(options[0]); }
              if (e.key === 'Backspace' && !text && picked.length) setPicked((cur) => cur.slice(0, -1));
            }} data-testid="share-people" />
        </div>
        <select className="select" aria-label="Access for the people being added" value={role} onChange={(e) => setRole(e.target.value as 'view' | 'edit')} data-testid="share-role">
          <option value="view">Can view</option><option value="edit">Can edit</option>
        </select>
        <button className="btn" disabled={!picked.length} onClick={add} data-testid="share-add-button">Add</button>
        {open && options.length > 0 && (
          <ul className="share-options" id="share-options" role="listbox" data-testid="share-options">
            {options.map((p) => (
              <li key={p.id} role="option" aria-selected={false} onMouseDown={(e) => { e.preventDefault(); pick(p); }}>
                <span>{p.name}</span><span className="faint">{p.email}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="share-list" data-testid="share-list">
        {shares.length === 0 && <p className="muted" style={{ margin: 0 }}>Not shared with anyone yet.</p>}
        {shares.map((s) => (
          <div key={s.userId} className="share-row" data-testid="share-row">
            <span>{s.name}</span>
            <select className="select" aria-label={`Access for ${s.name}`} value={s.canEdit ? 'edit' : 'view'}
              onChange={(e) => setShares((cur) => cur.map((x) => (x.userId === s.userId ? { ...x, canEdit: e.target.value === 'edit' } : x)))}>
              <option value="view">Can view</option><option value="edit">Can edit</option>
            </select>
            <button className="btn ghost sm" aria-label={`Remove ${s.name}`} onClick={() => setShares((cur) => cur.filter((x) => x.userId !== s.userId))} data-testid="share-remove">Remove</button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
